import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Text,
  Title,
  Metric,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Flex,
  Grid,
} from '@tremor/react';
import {
  BarChart as RechartsBarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import kpiAPI from '../../api/kpi';
import DatePicker from './DatePicker';
import './KPIStatPage.css';

const PROJECT = 'MAN';

const STORAGE_KEY_PS_DATE_FROM = 'ps-dateFrom';
const STORAGE_KEY_PS_DATE_TO = 'ps-dateTo';

function getInitialPsDateFrom() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_PS_DATE_FROM);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return formatDateForInput(d);
}

function getInitialPsDateTo() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_PS_DATE_TO);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  return formatDateForInput(new Date());
}

/** Filtruje wiersze po zakresie dat (DATA WPISU w [dateFrom, dateTo]). */
function filterRowsByDateRange(rows, headers, rangeFrom, rangeTo) {
  if (!rows.length || !rangeFrom || !rangeTo) return rows;
  const dateCol = getDateColumnIndex(headers);
  const from = new Date(rangeFrom);
  const to = new Date(rangeTo);
  to.setHours(23, 59, 59, 999);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    const t = d.getTime();
    return t >= from.getTime() && t <= to.getTime();
  });
}

/** Wykres czasu wg zakresu: jedna kategoria (dzień/miesiąc/rok) na słupek. */
function buildTimeChartDataByDateRange(filteredRows, headers, dateFrom, dateTo, rangeKey) {
  const dateCol = getDateColumnIndex(headers);
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (dateCol < 0 || iloscCol < 0 || !dateFrom || !dateTo) return [];
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  to.setHours(23, 59, 59, 999);
  if (rangeKey === 'day') {
    const byDay = new Map();
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      byDay.set(formatDateForInput(d), 0);
    }
    filteredRows.forEach((row) => {
      const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
      const d = parseDateFromCell(val);
      if (!d) return;
      const key = formatDateForInput(d);
      if (byDay.has(key)) byDay.set(key, (byDay.get(key) || 0) + parseNumber(row[iloscCol]));
    });
    return Array.from(byDay.entries()).map(([dateStr, value]) => ({
      label: dateStr,
      value,
      dateStr,
    }));
  }
  if (rangeKey === 'month') {
    const byMonth = new Map();
    let d = new Date(from.getFullYear(), from.getMonth(), 1);
    const end = new Date(to.getFullYear(), to.getMonth(), 1);
    while (d.getTime() <= end.getTime()) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      byMonth.set(key, { year: d.getFullYear(), month: d.getMonth() + 1, value: 0 });
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    filteredRows.forEach((row) => {
      const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
      const cellDate = parseDateFromCell(val);
      if (!cellDate) return;
      const key = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}`;
      const entry = byMonth.get(key);
      if (entry) entry.value += parseNumber(row[iloscCol]);
    });
    return Array.from(byMonth.values()).map((e) => ({
      label: `${MONTH_LABELS[e.month - 1]} ${e.year}`,
      value: e.value,
      year: e.year,
      month: e.month,
    }));
  }
  if (rangeKey === 'year') {
    const years = new Set();
    for (let y = from.getFullYear(); y <= to.getFullYear(); y++) years.add(y);
    const byYear = new Map();
    years.forEach((y) => byYear.set(y, 0));
    filteredRows.forEach((row) => {
      const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
      const cellDate = parseDateFromCell(val);
      if (!cellDate || !byYear.has(cellDate.getFullYear())) return;
      byYear.set(cellDate.getFullYear(), (byYear.get(cellDate.getFullYear()) || 0) + parseNumber(row[iloscCol]));
    });
    return Array.from(byYear.entries()).map(([year, value]) => ({ label: String(year), value, year }));
  }
  return [];
}

function filterRowsByDateStr(rows, headers, dateStr) {
  const dateCol = getDateColumnIndex(headers);
  const [y, m, day] = dateStr.split('-').map(Number);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    return d.getFullYear() === y && d.getMonth() === m - 1 && d.getDate() === day;
  });
}

function filterRowsByYear(rows, headers, year) {
  const dateCol = getDateColumnIndex(headers);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    return d && d.getFullYear() === year;
  });
}

/** Paleta kolorów dla wykresów – różne kolory dla kategorii (wyrazistość na ciemnym tle) */
const CHART_COLORS = [
  '#00bcd4', /* cyan */
  '#ff9800', /* pomarańcz */
  '#4caf50', /* zielony */
  '#f44336', /* czerwony */
  '#9c27b0', /* fiolet */
  '#2196f3', /* niebieski */
  '#ffeb3b', /* żółty */
  '#009688', /* teal */
  '#e91e63', /* różowy */
  '#3f51b5', /* indigo */
];

const PERIOD_OPTIONS = [
  { key: 'day', label: 'Na dzień' },
  { key: 'month', label: 'Na miesiąc' },
  { key: 'year', label: 'Na rok' },
];

/** Krótkie etykiety dla jednej kontroli „Zakres” */
const ZAKRES_OPTIONS = [
  { key: 'day', label: 'Dzień' },
  { key: 'month', label: 'Miesiąc' },
  { key: 'year', label: 'Rok' },
];

const KPI_CARDS = [
  { key: 'ilosc-wpisow', label: 'ILOŚĆ WPISÓW', value: '—' },
  { key: 'ps-sztuki', label: 'PS SZTUKI', value: '—' },
  { key: 'ps-seq', label: 'PS SEQ', value: '—' },
  { key: 'ps-rep', label: 'PS REP', value: '—' },
  { key: 'montaz', label: 'MONTAŻ', value: '—' },
  { key: 'bez-montazu', label: 'BEZ MONTAŻU', value: '—' },
];

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((row) => {
    if (!Array.isArray(row)) return [{ value: '' }];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function formatDateForInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parsuje komórkę jako datę i opcjonalnie godzinę. Zwraca Date z czasem lub null. */
function parseDateFromCell(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;

  const timeMatch = s.match(/\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*$/);
  let datePart = s;
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  if (timeMatch) {
    datePart = s.slice(0, s.length - timeMatch[0].length).trim();
    hours = parseInt(timeMatch[1], 10) || 0;
    minutes = parseInt(timeMatch[2], 10) || 0;
    seconds = parseInt(timeMatch[3], 10) || 0;
  }

  let d = null;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) d = new Date(iso[0]);
  else {
    const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
    if (ddmmyy) d = new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
    else {
      const fallback = new Date(datePart);
      d = Number.isNaN(fallback.getTime()) ? null : fallback;
    }
  }
  if (!d || Number.isNaN(d.getTime())) return null;
  if (timeMatch) d.setHours(hours, minutes, seconds, 0);
  return d;
}

function getDateColumnIndex(headers) {
  const names = headers.map((h) => String(h || '').trim().toUpperCase());
  const idx = names.findIndex(
    (n) => n === 'DATA WPISU' || n.includes('DATA WPISU') || n === 'DATA' || n === 'DATE' || n.startsWith('DATA')
  );
  return idx >= 0 ? idx : 0;
}

function getColumnIndex(headers, headerName) {
  const needle = String(headerName || '').trim().toLowerCase();
  const idx = headers.findIndex((h) => String(h || '').trim().toLowerCase() === needle);
  return idx >= 0 ? idx : -1;
}

function getDataMontazuColumnIndex(headers) {
  let idx = getColumnIndex(headers, 'Data montażu');
  if (idx < 0) idx = getColumnIndex(headers, 'Data montazu');
  return idx;
}

function parseNumber(val) {
  if (val == null || val === '') return 0;
  const v = typeof val === 'object' && val !== null && 'value' in val ? val.value : val;
  const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

function computeKpiValues(psSheet, seqSheet, headers, filteredRows) {
  const iloscWpisow = filteredRows.length;
  let psSztuki = 0;
  let psSeq = 0;
  let psRep = 0;
  let montaz = 0;
  let bezMontazu = 0;

  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  const psaCol = getColumnIndex(headers, 'PSA');
  const dataWpisuCol = getDateColumnIndex(headers);
  const dataMontazuCol = getDataMontazuColumnIndex(headers);
  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

  const seqPsaSet = new Set();
  if (seqSheet?.data?.length) {
    const seqHeaders = seqSheet.data[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
    const seqPsaCol = getColumnIndex(seqHeaders, 'PSA');
    if (seqPsaCol >= 0) {
      seqSheet.data.slice(1).forEach((row) => {
        const v = (row[seqPsaCol] && typeof row[seqPsaCol] === 'object' ? row[seqPsaCol].value : row[seqPsaCol]) ?? '';
        const normalized = String(v).trim().toLowerCase();
        if (normalized) seqPsaSet.add(normalized);
      });
    }
  }

  filteredRows.forEach((row) => {
    const ilosc = iloscCol >= 0 ? parseNumber(row[iloscCol]) : 0;
    psSztuki += ilosc;

    const psaVal = psaCol >= 0 ? (row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol]) ?? '' : '';
    const psaNormalized = String(psaVal).trim().toLowerCase();

    if (psaNormalized && seqPsaSet.has(psaNormalized)) {
      psSeq += ilosc;
    } else {
      psRep += ilosc;
    }

    if (dataMontazuCol >= 0) {
      const wpisuVal = row[dataWpisuCol] && typeof row[dataWpisuCol] === 'object' ? row[dataWpisuCol].value : row[dataWpisuCol];
      const montazVal = row[dataMontazuCol] && typeof row[dataMontazuCol] === 'object' ? row[dataMontazuCol].value : row[dataMontazuCol];
      const dateWpisu = parseDateFromCell(wpisuVal);
      const dateMontazu = parseDateFromCell(montazVal);
      const hasSameDate =
        dateWpisu &&
        dateMontazu &&
        dateWpisu.getFullYear() === dateMontazu.getFullYear() &&
        dateWpisu.getMonth() === dateMontazu.getMonth() &&
        dateWpisu.getDate() === dateMontazu.getDate();
      const withinTwoHours =
        hasSameDate && Math.abs(dateMontazu.getTime() - dateWpisu.getTime()) <= TWO_HOURS_MS;
      if (withinTwoHours) montaz += ilosc;
      else bezMontazu += ilosc;
    }
  });

  return {
    iloscWpisow,
    psSztuki,
    psSeq,
    psRep,
    montaz: dataMontazuCol >= 0 ? montaz : '—',
    bezMontazu: dataMontazuCol >= 0 ? bezMontazu : '—',
  };
}

/** Zwraca OPIS z arkusza SEQ dla danego PSA (porównanie bez rozróżniania wielkości liter). */
function getOpisFromSeq(seqSheet, psaValue) {
  if (!seqSheet?.data?.length || psaValue == null) return null;
  const headers = seqSheet.data[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
  const psaCol = getColumnIndex(headers, 'PSA');
  const opisCol = getColumnIndex(headers, 'OPIS');
  if (psaCol < 0 || opisCol < 0) return null;
  const needle = String(psaValue).trim().toLowerCase();
  if (!needle) return null;
  const opisy = [];
  seqSheet.data.slice(1).forEach((row) => {
    const psa = (row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol]) ?? '';
    if (String(psa).trim().toLowerCase() !== needle) return;
    const opis = (row[opisCol] && typeof row[opisCol] === 'object' ? row[opisCol].value : row[opisCol]) ?? '';
    const s = String(opis).trim();
    if (s && !opisy.includes(s)) opisy.push(s);
  });
  return opisy.length ? opisy.join(' · ') : null;
}

/** Dane do wykresu poziomego: Y = Opis Kodowania (kategorie), X = suma ILOŚĆ (PS SZTUKI) */
/** Tooltip czytelny: jasne tło, ciemny tekst, nagłówek + kolorowa kropka + etykieta: wartość */
function OpisKodowaniaTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value;
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{label}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-cyan" />
        <span>PS SZTUKI: {value}</span>
      </div>
    </div>
  );
}

/** Tooltip dla wykresu czasu (PS SZTUKI wg miesiąca/roku). */
function TimeChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? '—';
  const name = payload[0]?.payload?.label ?? label ?? '—';
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{name}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-cyan" />
        <span>PS SZTUKI: {value}</span>
      </div>
    </div>
  );
}

/** Tooltip dla PS według PSA: nagłówek PSA, ILOŚĆ, OPIS z arkusza SEQ */
function PsaTooltip({ active, payload, label, seqSheet }) {
  if (!active || !payload?.length) return null;
  const psa = payload[0]?.payload?.PSA ?? label ?? '—';
  const ilosc = payload[0]?.value ?? '—';
  const opisSeq = getOpisFromSeq(seqSheet, psa);
  const headerText = opisSeq ? `${psa} – ${opisSeq}` : psa;
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{headerText}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-cyan" />
        <span>ILOŚĆ: {ilosc}</span>
      </div>
    </div>
  );
}

function buildOpisKodowaniaBarData(filteredRows, headers) {
  const opisCol = getColumnIndex(headers, 'Opis Kodowania');
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (opisCol < 0 || iloscCol < 0) return [];
  const byOpis = new Map();
  filteredRows.forEach((row) => {
    const opisVal = row[opisCol] && typeof row[opisCol] === 'object' ? row[opisCol].value : row[opisCol];
    const name = String(opisVal ?? '').trim() || '(pusty)';
    const value = parseNumber(row[iloscCol]);
    byOpis.set(name, (byOpis.get(name) || 0) + value);
  });
  return Array.from(byOpis.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

const MONTH_LABELS = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

/** Na dzień: jeden słupek (wybrany dzień), wartość nad słupkiem. */
function buildTimeChartDataByDay(filteredRows, headers, selectedDayISO) {
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (iloscCol < 0) return [];
  const sum = filteredRows.reduce((s, row) => s + parseNumber(row[iloscCol]), 0);
  const label = selectedDayISO
    ? (() => {
        const [y, m, d] = selectedDayISO.split('-').map(Number);
        return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
      })()
    : '—';
  return [{ label, value: sum }];
}

/** Na miesiąc: oś X = dni 1..N (bieżący miesiąc), oś Y = PS SZTUKI, wartość nad słupkiem. month = 1–12. */
function buildTimeChartDataByMonth(filteredRows, headers, year, month) {
  const dateCol = getDateColumnIndex(headers);
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (dateCol < 0 || iloscCol < 0) return [];
  // W JS Date: month 0=styczeń, 1=luty… new Date(year, month, 0) = ostatni dzień (month-1), więc dla month 1–12: ostatni dzień = new Date(year, month, 0).getDate()
  const lastDay = new Date(year, month, 0).getDate();
  const byDay = new Map();
  for (let d = 1; d <= lastDay; d++) byDay.set(d, 0);
  filteredRows.forEach((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d || d.getFullYear() !== year || d.getMonth() !== month - 1) return;
    const day = d.getDate();
    byDay.set(day, (byDay.get(day) || 0) + parseNumber(row[iloscCol]));
  });
  return Array.from({ length: lastDay }, (_, i) => i + 1).map((day) => ({
    label: String(day),
    day,
    value: byDay.get(day) || 0,
  }));
}

/** Na rok: oś X = miesiące (Sty, Lut, …), oś Y = PS SZTUKI, wartość nad słupkiem. */
function buildTimeChartDataByYear(filteredRows, headers, year) {
  const dateCol = getDateColumnIndex(headers);
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (dateCol < 0 || iloscCol < 0) return [];
  const byMonth = new Map();
  for (let m = 0; m < 12; m++) byMonth.set(m, 0);
  filteredRows.forEach((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d || d.getFullYear() !== year) return;
    const m = d.getMonth();
    byMonth.set(m, (byMonth.get(m) || 0) + parseNumber(row[iloscCol]));
  });
  return MONTH_LABELS.map((name, m) => ({
    label: name,
    month: m + 1,
    value: byMonth.get(m) || 0,
  }));
}

function buildPsaBarData(filteredRows, headers) {
  const psaCol = getColumnIndex(headers, 'PSA');
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (psaCol < 0 || iloscCol < 0) return [];
  const byPsa = new Map();
  filteredRows.forEach((row) => {
    const psaVal = row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol];
    const label = String(psaVal ?? '').trim() || '(pusty)';
    const ilosc = parseNumber(row[iloscCol]);
    byPsa.set(label, (byPsa.get(label) || 0) + ilosc);
  });
  return Array.from(byPsa.entries())
    .map(([PSA, ILOŚĆ]) => ({ PSA, ILOŚĆ }))
    .sort((a, b) => b.ILOŚĆ - a.ILOŚĆ);
}

/** Filtruje wiersze do konkretnego dnia (rok, miesiąc 1–12, dzień 1–31). */
function filterRowsByDay(rows, headers, year, month, day) {
  const dateCol = getDateColumnIndex(headers);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
  });
}

/** Filtruje wiersze do konkretnego miesiąca (rok, miesiąc 1–12). */
function filterRowsByMonth(rows, headers, year, month) {
  const dateCol = getDateColumnIndex(headers);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    return d.getFullYear() === year && d.getMonth() === month - 1;
  });
}

/** Filtruje wiersze po wyborze z wykresu (Opis Kodowania i/lub PSA). */
function filterRowsByBarSelection(rows, headers, selectedOpisKodowania, selectedPsa) {
  if (!selectedOpisKodowania && !selectedPsa) return rows;
  const opisCol = getColumnIndex(headers, 'Opis Kodowania');
  const psaCol = getColumnIndex(headers, 'PSA');
  return rows.filter((row) => {
    if (selectedOpisKodowania && opisCol >= 0) {
      const val = row[opisCol] && typeof row[opisCol] === 'object' ? row[opisCol].value : row[opisCol];
      if (String(val ?? '').trim() !== selectedOpisKodowania) return false;
    }
    if (selectedPsa && psaCol >= 0) {
      const val = row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol];
      if (String(val ?? '').trim() !== selectedPsa) return false;
    }
    return true;
  });
}

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

/** Zwraca zbiór PSA z arkusza SEQ (małe litery). */
function getSeqPsaSet(seqSheet) {
  const set = new Set();
  if (!seqSheet?.data?.length) return set;
  const seqHeaders = seqSheet.data[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
  const seqPsaCol = getColumnIndex(seqHeaders, 'PSA');
  if (seqPsaCol < 0) return set;
  seqSheet.data.slice(1).forEach((row) => {
    const v = (row[seqPsaCol] && typeof row[seqPsaCol] === 'object' ? row[seqPsaCol].value : row[seqPsaCol]) ?? '';
    const normalized = String(v).trim().toLowerCase();
    if (normalized) set.add(normalized);
  });
  return set;
}

/** Filtruje wiersze według wybranego KPI (klik kafelka). Zwraca tylko wiersze wchodzące w skład danego KPI. */
function filterRowsByKpi(rows, headers, seqSheet, kpiKey) {
  if (!kpiKey || !rows.length) return rows;
  if (kpiKey === 'ilosc-wpisow' || kpiKey === 'ps-sztuki') return rows;

  const psaCol = getColumnIndex(headers, 'PSA');
  const dataWpisuCol = getDateColumnIndex(headers);
  const dataMontazuCol = getDataMontazuColumnIndex(headers);
  const seqPsaSet = getSeqPsaSet(seqSheet);

  if (kpiKey === 'ps-seq') {
    return rows.filter((row) => {
      const psaVal = psaCol >= 0 ? (row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol]) ?? '' : '';
      return seqPsaSet.has(String(psaVal).trim().toLowerCase());
    });
  }
  if (kpiKey === 'ps-rep') {
    return rows.filter((row) => {
      const psaVal = psaCol >= 0 ? (row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol]) ?? '' : '';
      const normalized = String(psaVal).trim().toLowerCase();
      return !normalized || !seqPsaSet.has(normalized);
    });
  }
  if (kpiKey === 'montaz') {
    return rows.filter((row) => {
      const wpisuVal = row[dataWpisuCol] && typeof row[dataWpisuCol] === 'object' ? row[dataWpisuCol].value : row[dataWpisuCol];
      const montazVal = row[dataMontazuCol] && typeof row[dataMontazuCol] === 'object' ? row[dataMontazuCol].value : row[dataMontazuCol];
      const dateWpisu = parseDateFromCell(wpisuVal);
      const dateMontazu = parseDateFromCell(montazVal);
      const hasSameDate =
        dateWpisu &&
        dateMontazu &&
        dateWpisu.getFullYear() === dateMontazu.getFullYear() &&
        dateWpisu.getMonth() === dateMontazu.getMonth() &&
        dateWpisu.getDate() === dateMontazu.getDate();
      return hasSameDate && Math.abs(dateMontazu.getTime() - dateWpisu.getTime()) <= TWO_HOURS_MS;
    });
  }
  if (kpiKey === 'bez-montazu') {
    return rows.filter((row) => {
      const wpisuVal = row[dataWpisuCol] && typeof row[dataWpisuCol] === 'object' ? row[dataWpisuCol].value : row[dataWpisuCol];
      const montazVal = row[dataMontazuCol] && typeof row[dataMontazuCol] === 'object' ? row[dataMontazuCol].value : row[dataMontazuCol];
      const dateWpisu = parseDateFromCell(wpisuVal);
      const dateMontazu = parseDateFromCell(montazVal);
      const hasSameDate =
        dateWpisu &&
        dateMontazu &&
        dateWpisu.getFullYear() === dateMontazu.getFullYear() &&
        dateWpisu.getMonth() === dateMontazu.getMonth() &&
        dateWpisu.getDate() === dateMontazu.getDate();
      const withinTwoHours =
        hasSameDate && Math.abs(dateMontazu.getTime() - dateWpisu.getTime()) <= TWO_HOURS_MS;
      return !withinTwoHours;
    });
  }
  return rows;
}

function filterRowsByPeriod(rows, headers, rangeKey, selectedDayISO, selectedYear, selectedMonth) {
  if (!rows.length) return rows;
  const dateCol = getDateColumnIndex(headers);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const year = selectedYear != null ? selectedYear : now.getFullYear();
  const month = selectedMonth != null ? selectedMonth : now.getMonth() + 1;

  let dayStart = yesterdayStart;
  if (rangeKey === 'day' && selectedDayISO) {
    const [y, m, d] = selectedDayISO.split('-').map(Number);
    dayStart = new Date(y, m - 1, d);
  }
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 1);
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year + 1, 0, 1);

  return rows.filter((row) => {
    const cell = row[dateCol];
    const val = cell && typeof cell === 'object' ? cell.value : cell;
    const d = parseDateFromCell(val);
    if (!d) return false;
    const t = d.getTime();
    if (rangeKey === 'day') {
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);
      return t >= dayStart.getTime() && t < dayEnd.getTime();
    }
    if (rangeKey === 'month') {
      return t >= monthStart.getTime() && t < monthEnd.getTime();
    }
    if (rangeKey === 'year') {
      return t >= yearStart.getTime() && t < yearEnd.getTime();
    }
    return true;
  });
}

const MONTH_OPTIONS = [
  { value: 1, label: 'Styczeń' },
  { value: 2, label: 'Luty' },
  { value: 3, label: 'Marzec' },
  { value: 4, label: 'Kwiecień' },
  { value: 5, label: 'Maj' },
  { value: 6, label: 'Czerwiec' },
  { value: 7, label: 'Lipiec' },
  { value: 8, label: 'Sierpień' },
  { value: 9, label: 'Wrzesień' },
  { value: 10, label: 'Październik' },
  { value: 11, label: 'Listopad' },
  { value: 12, label: 'Grudzień' },
];

function PS() {
  const [dateFrom, setDateFrom] = useState(getInitialPsDateFrom);
  const [dateTo, setDateTo] = useState(getInitialPsDateTo);
  const [rangeKey, setRangeKey] = useState('day');
  const [selectedTimeChartDateStr, setSelectedTimeChartDateStr] = useState(null);
  const [selectedTimeChartMonth, setSelectedTimeChartMonth] = useState(null);
  const [selectedTimeChartYear, setSelectedTimeChartYear] = useState(null);
  const [selectedOpisKodowania, setSelectedOpisKodowania] = useState(null);
  const [selectedPsa, setSelectedPsa] = useState(null);
  const [selectedKpiKey, setSelectedKpiKey] = useState(null);
  const [psSheet, setPsSheet] = useState(null);
  const [seqSheet, setSeqSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReports = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      const ps = list.find((s) => String(s.name || '').trim() === 'PS');
      const seq = list.find((s) => String(s.name || '').trim() === 'SEQ');
      setPsSheet(ps ? { name: ps.name, data: normalizeSheetData(ps.data) } : null);
      setSeqSheet(seq ? { name: seq.name, data: normalizeSheetData(seq.data) } : null);
      if (!ps) setError('Nie udało się załadować danych z arkusza PS.');
    } catch (err) {
      console.error('Błąd ładowania raportu PS:', err);
      setError('Nie udało się załadować danych z arkusza PS.');
      setPsSheet(null);
      setSeqSheet(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    try {
      if (dateFrom) localStorage.setItem(STORAGE_KEY_PS_DATE_FROM, dateFrom);
      if (dateTo) localStorage.setItem(STORAGE_KEY_PS_DATE_TO, dateTo);
    } catch (e) {}
  }, [dateFrom, dateTo]);

  const headers = psSheet?.data?.[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
  const dataRows = psSheet?.data?.slice(1) ?? [];
  const filteredRows = filterRowsByDateRange(dataRows, headers, dateFrom, dateTo);

  const kpiValues = psSheet
    ? computeKpiValues(psSheet, seqSheet, headers, filteredRows)
    : { iloscWpisow: '—', psSztuki: '—', psSeq: '—', psRep: '—', montaz: '—', bezMontazu: '—' };

  const kpiCards = [
    { key: 'ilosc-wpisow', label: 'ILOŚĆ WPISÓW', value: kpiValues.iloscWpisow },
    { key: 'ps-sztuki', label: 'PS SZTUKI', value: kpiValues.psSztuki },
    { key: 'ps-seq', label: 'PS SEQ', value: kpiValues.psSeq },
    { key: 'ps-rep', label: 'PS REP', value: kpiValues.psRep },
    { key: 'montaz', label: 'MONTAŻ', value: kpiValues.montaz },
    { key: 'bez-montazu', label: 'BEZ MONTAŻU', value: kpiValues.bezMontazu },
  ];

  const rowsForCharts =
    rangeKey === 'day' && selectedTimeChartDateStr
      ? filterRowsByDateStr(filteredRows, headers, selectedTimeChartDateStr)
      : rangeKey === 'month' && selectedTimeChartMonth != null && selectedTimeChartYear != null
        ? filterRowsByMonth(filteredRows, headers, selectedTimeChartYear, selectedTimeChartMonth)
        : rangeKey === 'year' && selectedTimeChartYear != null
          ? filterRowsByYear(filteredRows, headers, selectedTimeChartYear)
          : filteredRows;
  const chartOpisKodowaniaData = psSheet ? buildOpisKodowaniaBarData(rowsForCharts, headers) : [];
  const chartPsaData = psSheet ? buildPsaBarData(rowsForCharts, headers) : [];
  const chartValueFormatter = (v) => (v != null ? String(v) : '');

  const timeChartData =
    psSheet && headers.length
      ? buildTimeChartDataByDateRange(filteredRows, headers, dateFrom, dateTo, rangeKey)
      : [];

  /* Tabela używa tej samej bazy co wykresy „PS według kodowania” i „PS według PSA” – po kliknięciu dnia/miesiąca w wykresie czasu tabela też jest filtrowana */
  const rowsAfterKpiFilter = filterRowsByKpi(rowsForCharts, headers, seqSheet, selectedKpiKey);
  const tableRows = filterRowsByBarSelection(
    rowsAfterKpiFilter,
    headers,
    selectedOpisKodowania,
    selectedPsa
  );
  const hasBarFilter = selectedOpisKodowania != null || selectedPsa != null;
  const hasKpiFilter = selectedKpiKey != null;
  const clearBarFilter = () => {
    setSelectedOpisKodowania(null);
    setSelectedPsa(null);
  };
  const selectedKpiLabel = hasKpiFilter ? kpiCards.find((k) => k.key === selectedKpiKey)?.label : null;

  return (
    <div className="kpi-stat-page">
      {/* Nagłówek: PS (lewo) + Zakres dat Od/Do (jak Transport) + grupowanie wykresu czasu */}
      <div className="kpi-ps-header-bar">
        <Title className="kpi-ps-header-title">PS</Title>
        <div className="kpi-ps-zakres">
          <span className="kpi-ps-zakres-title">Zakres dat</span>
          <div className="kpi-ps-zakres-inner">
            <DatePicker
              label="Od:"
              value={dateFrom}
              onChange={(v) => setDateFrom(v || '')}
              max={dateTo || undefined}
              placeholder="Od"
              inline
              align="left"
              className="kpi-ps-zakres-date"
            />
            <DatePicker
              label="Do:"
              value={dateTo}
              onChange={(v) => setDateTo(v || '')}
              min={dateFrom || undefined}
              placeholder="Do"
              inline
              align="right"
              className="kpi-ps-zakres-date"
            />
          </div>
          <span className="kpi-ps-zakres-title" style={{ marginLeft: 8 }}>Wykres czasu</span>
          <div className="kpi-ps-zakres-buttons" role="group" aria-label="Grupowanie wykresu czasu">
            {ZAKRES_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`kpi-ps-zakres-btn ${rangeKey === o.key ? 'kpi-ps-zakres-btn-active' : ''}`}
                onClick={() => {
                  setRangeKey(o.key);
                  setSelectedTimeChartDateStr(null);
                  setSelectedTimeChartMonth(null);
                  setSelectedTimeChartYear(null);
                }}
                aria-pressed={rangeKey === o.key}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Kafelki KPI – osobna sekcja */}
      <Card className="kpi-ps-shell">
        <Grid numItemsSm={2} numItemsMd={3} numItemsLg={6} className="kpi-ps-cards">
          {kpiCards.map((kpi) => (
            <Card
              key={kpi.key}
              className={`kpi-ps-kpi-card ${selectedKpiKey === kpi.key ? 'kpi-ps-kpi-card-selected' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedKpiKey((prev) => (prev === kpi.key ? null : kpi.key))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedKpiKey((prev) => (prev === kpi.key ? null : kpi.key));
                }
              }}
            >
              <Text className="kpi-ps-kpi-label">{kpi.label}</Text>
              <Metric className="kpi-ps-kpi-metric">{kpi.value}</Metric>
            </Card>
          ))}
        </Grid>
      </Card>

      {/* Duży prostokąt – obszar na wykresy (Page Shell 03) */}
      <Card className="kpi-ps-charts-area">
        {/* Wykres czasu: Na miesiąc = oś X dni 1..N, Na rok = oś X miesiące (Sty, Lut…), wartość nad słupkiem. */}
        <div className="kpi-ps-chart-time-block">
          <Text className="kpi-ps-chart-block-title">PS SZTUKI wg czasu</Text>
          {timeChartData.length === 0 ? (
            <div className="kpi-ps-chart-empty">Brak danych do wykresu czasu w wybranym zakresie.</div>
          ) : (
            <div
              className="kpi-ps-chart-time"
              onMouseDown={() => {
                const el = document.activeElement;
                if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
              }}
              onMouseUp={() => {
                setTimeout(() => {
                  const el = document.activeElement;
                  if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                }, 0);
              }}
            >
              <ResponsiveContainer width="100%" height={280}>
                <RechartsBarChart
                  data={timeChartData}
                  margin={{ top: 28, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis
                    dataKey="label"
                    angle={-45}
                    textAnchor="end"
                    height={48}
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    interval={0}
                  />
                  <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip content={<TimeChartTooltip />} cursor={false} />
                  <Bar
                    dataKey="value"
                    name="PS SZTUKI"
                    fill={CHART_COLORS[0]}
                    radius={[4, 4, 0, 0]}
                    barSize={24}
                    onClick={
                      rangeKey === 'day'
                        ? (data) => setSelectedTimeChartDateStr((prev) => (prev === data.dateStr ? null : data.dateStr))
                        : rangeKey === 'month'
                          ? (data) => {
                              const same = selectedTimeChartMonth === data.month && selectedTimeChartYear === data.year;
                              setSelectedTimeChartMonth(same ? null : data.month);
                              setSelectedTimeChartYear(same ? null : data.year);
                            }
                          : rangeKey === 'year'
                            ? (data) => setSelectedTimeChartYear((prev) => (prev === data.year ? null : data.year))
                            : undefined
                    }
                    style={rangeKey === 'day' || rangeKey === 'month' || rangeKey === 'year' ? { cursor: 'pointer' } : undefined}
                  >
                    {timeChartData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                    <LabelList dataKey="value" position="top" fill="#94a3b8" fontSize={11} formatter={(v) => (v > 0 ? String(Math.round(Number(v))) : '')} />
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {rangeKey === 'day' && selectedTimeChartDateStr && (
          <div className="kpi-ps-table-filter-hint kpi-ps-time-chart-day-hint">
            <span>Dane za dzień {selectedTimeChartDateStr} (wybór z wykresu „PS SZTUKI wg czasu”).</span>
            <Button size="xs" variant="secondary" onClick={() => setSelectedTimeChartDateStr(null)} className="kpi-ps-table-clear-filter">
              Pokaż cały zakres
            </Button>
          </div>
        )}
        {rangeKey === 'month' && selectedTimeChartMonth != null && selectedTimeChartYear != null && (
          <div className="kpi-ps-table-filter-hint kpi-ps-time-chart-day-hint">
            <span>Dane za miesiąc {MONTH_LABELS[selectedTimeChartMonth - 1]} {selectedTimeChartYear} (wybór z wykresu „PS SZTUKI wg czasu”).</span>
            <Button size="xs" variant="secondary" onClick={() => { setSelectedTimeChartMonth(null); setSelectedTimeChartYear(null); }} className="kpi-ps-table-clear-filter">
              Pokaż cały zakres
            </Button>
          </div>
        )}
        {rangeKey === 'year' && selectedTimeChartYear != null && (
          <div className="kpi-ps-table-filter-hint kpi-ps-time-chart-day-hint">
            <span>Dane za rok {selectedTimeChartYear} (wybór z wykresu „PS SZTUKI wg czasu”).</span>
            <Button size="xs" variant="secondary" onClick={() => setSelectedTimeChartYear(null)} className="kpi-ps-table-clear-filter">
              Pokaż cały zakres
            </Button>
          </div>
        )}
        <div className="kpi-ps-charts-grid">
          <div className="kpi-ps-chart-block">
            <Text className="kpi-ps-chart-block-title">PS według kodowania</Text>
            {chartOpisKodowaniaData.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych w wybranym zakresie.</div>
            ) : (
              <div
                className="kpi-ps-chart-opis"
                onMouseDown={() => {
                  const el = document.activeElement;
                  if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                }}
                onMouseUp={() => {
                  setTimeout(() => {
                    const el = document.activeElement;
                    if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                  }, 0);
                }}
              >
                <ResponsiveContainer width="100%" height={Math.max(240, chartOpisKodowaniaData.length * 36)}>
                  <RechartsBarChart
                    layout="vertical"
                    data={chartOpisKodowaniaData}
                    margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <YAxis type="category" dataKey="name" width={220} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip content={<OpisKodowaniaTooltip />} cursor={false} />
                    <Bar
                      dataKey="value"
                      name="PS SZTUKI"
                      radius={[0, 0, 0, 0]}
                      barSize={24}
                      onClick={(data) => setSelectedOpisKodowania((prev) => (prev === data.name ? null : data.name))}
                      style={{ cursor: 'pointer' }}
                    >
                      {chartOpisKodowaniaData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="kpi-ps-chart-block">
            <Text className="kpi-ps-chart-block-title">PS według PSA</Text>
            {chartPsaData.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych w wybranym zakresie.</div>
            ) : (
              <div
                className="kpi-ps-chart-psa kpi-ps-chart-psa-rounded"
                onMouseDown={() => {
                  const el = document.activeElement;
                  if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                }}
                onMouseUp={() => {
                  setTimeout(() => {
                    const el = document.activeElement;
                    if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                  }, 0);
                }}
              >
                <ResponsiveContainer width="100%" height={280}>
                  <RechartsBarChart
                    data={chartPsaData}
                    margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                    <XAxis dataKey="PSA" angle={-45} textAnchor="end" height={48} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <Tooltip content={<PsaTooltip seqSheet={seqSheet} />} cursor={false} />
                    <Bar
                      dataKey="ILOŚĆ"
                      name="ILOŚĆ"
                      radius={[4, 4, 0, 0]}
                      barSize={24}
                      onClick={(data) => setSelectedPsa((prev) => (prev === data.PSA ? null : data.PSA))}
                      style={{ cursor: 'pointer' }}
                    >
                      {chartPsaData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Table Action 3 – dane z arkusza PS (Uzupełnij raport), filtrowane po zakresie i po kliknięciu w wykres */}
      <Card className="kpi-ps-table-card">
        <Title className="kpi-ps-table-title">Błędy – lista</Title>
        {hasKpiFilter && (
          <div className="kpi-ps-table-filter-hint">
            <span>KPI: {selectedKpiLabel}. Tabela pokazuje tylko pozycje wchodzące w skład tego KPI.</span>
            <Button size="xs" variant="secondary" onClick={() => setSelectedKpiKey(null)} className="kpi-ps-table-clear-filter">
              Wyczyść filtr KPI
            </Button>
          </div>
        )}
        {hasBarFilter && (
          <div className="kpi-ps-table-filter-hint">
            <span>
              {selectedOpisKodowania && `Opis Kodowania: ${selectedOpisKodowania}`}
              {selectedOpisKodowania && selectedPsa && ' · '}
              {selectedPsa && `PSA: ${selectedPsa}`}
            </span>
            <Button size="xs" variant="secondary" onClick={clearBarFilter} className="kpi-ps-table-clear-filter">
              Wyczyść filtr
            </Button>
          </div>
        )}
        {error && <Text className="kpi-ps-table-error">{error}</Text>}
        {loading ? (
          <Text className="kpi-ps-table-loading">Ładowanie…</Text>
        ) : !psSheet ? (
          <Text className="kpi-ps-table-empty">Brak arkusza „PS” w Uzupełnij raport. Dodaj arkusz o nazwie PS i wypełnij dane.</Text>
        ) : (
          <Table className="kpi-ps-table">
            <TableHead>
              <TableRow>
                {headers.map((h, i) => (
                  <TableHeaderCell key={i}>{h || `Kolumna ${i + 1}`}</TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headers.length} className="kpi-ps-table-empty-cell">
                    {filteredRows.length === 0
                      ? 'Brak wpisów w wybranym zakresie.'
                      : hasKpiFilter
                        ? `Brak wpisów dla KPI „${selectedKpiLabel}”. Wyczyść filtr KPI lub wybierz inny kafelek.`
                        : 'Brak wpisów spełniających wybór z wykresu. Kliknij inny słupek lub „Wyczyść filtr”.'}
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {row.map((cell, c) => (
                      <TableCell key={c}>{cell && typeof cell === 'object' ? cell.value : cell}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export default PS;
